import * as BrowserWorker from "@effect/platform-browser/BrowserWorker";
import * as Cause from "effect/Cause";
import * as Deferred from "effect/Deferred";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Scope from "effect/Scope";
import * as Schema from "effect/Schema";
import * as Semaphore from "effect/Semaphore";
import { RpcClient } from "effect/rpc";
import { ClientRpcs, NativeFileDescriptor, PreviewFrameDescriptor, PROTOCOL_VERSION, MAX_ASSET_INPUT_BYTES } from "@linempo/worker-protocol";
import { NativeFileSlot } from "./native.js";
import { asRenderError, workerError } from "./model.js";
/** The only production transport: two native channels, with Effect RPC on rpcPort. */
export const openWorkerConnection = (options) => Effect.gen(function* () {
    const scope = yield* Effect.scope;
    const worker = yield* Effect.try({ try: () => options.createWorker(options.kind), catch: (error) => workerError("unavailable", String(error)) });
    const rpcChannel = new MessageChannel();
    const frameChannel = new MessageChannel();
    const failure = Deferred.makeUnsafe();
    const files = new NativeFileSlot();
    let closed = false;
    const terminate = (error) => {
        if (closed)
            return;
        closed = true;
        worker.terminate();
        options.onFailure(error);
        files.fail(error);
        Deferred.doneUnsafe(failure, Effect.fail(error));
    };
    const onError = (event) => terminate(workerError("unavailable", typeof ErrorEvent !== "undefined" && event instanceof ErrorEvent ? event.message || "Worker crashed" : "Worker failed"));
    const onMessageError = () => terminate(workerError("protocol", "Worker message could not be decoded"));
    const onMessage = (event) => {
        if (files.accept(event.data))
            return;
        const value = event.data;
        if (!value || typeof value !== "object" || !("bitmap" in value))
            return;
        const bitmap = value.bitmap;
        if (!bitmap || typeof bitmap.close !== "function") {
            terminate(workerError("protocol", "Invalid preview bitmap"));
            return;
        }
        try {
            if (closed) {
                bitmap.close();
                return;
            }
            const descriptor = Schema.decodeUnknownSync(PreviewFrameDescriptor)(value.descriptor);
            if (!(bitmap instanceof ImageBitmap) || bitmap.width !== descriptor.dimensions.width || bitmap.height !== descriptor.dimensions.height)
                throw new TypeError("Preview bitmap dimensions mismatch");
            options.onFrame({ descriptor, bitmap });
        }
        catch (error) {
            bitmap.close();
            terminate(workerError("protocol", `Invalid preview transfer: ${String(error)}`));
        }
    };
    worker.addEventListener("error", onError);
    worker.addEventListener("messageerror", onMessageError);
    rpcChannel.port1.addEventListener("messageerror", onMessageError);
    frameChannel.port1.addEventListener("messageerror", onMessageError);
    frameChannel.port1.addEventListener("message", onMessage);
    frameChannel.port1.start();
    yield* Effect.addFinalizer(() => Effect.sync(() => {
        closed = true;
        files.fail(workerError("unavailable", "Worker scope closed"));
        Deferred.doneUnsafe(failure, Effect.fail(workerError("unavailable", "Worker scope closed")));
        worker.removeEventListener("error", onError);
        worker.removeEventListener("messageerror", onMessageError);
        rpcChannel.port1.removeEventListener("messageerror", onMessageError);
        frameChannel.port1.removeEventListener("messageerror", onMessageError);
        frameChannel.port1.removeEventListener("message", onMessage);
        rpcChannel.port1.close();
        rpcChannel.port2.close();
        frameChannel.port1.close();
        frameChannel.port2.close();
        worker.terminate();
    }));
    yield* Effect.try({ try: () => worker.postMessage({ descriptor: { protocolVersion: PROTOCOL_VERSION, kind: options.kind }, rpcPort: rpcChannel.port2, framePort: frameChannel.port2 }, [rpcChannel.port2, frameChannel.port2]), catch: (error) => workerError("protocol", `Worker bootstrap failed: ${String(error)}`) });
    const protocol = RpcClient.layerProtocolWorker({ size: 1, concurrency: 16 }).pipe(Layer.provide(BrowserWorker.layer(() => rpcChannel.port1)));
    const context = yield* Layer.buildWithScope(protocol, scope).pipe(Effect.mapError(asRenderError));
    const rawRpc = yield* RpcClient.make(ClientRpcs).pipe(Effect.provideContext(context));
    // Keep half of the fixed 16 transport slots available for Ping/Cancel and render/seek.
    // Admission happens before the transport pool, so waiting workspace calls cannot starve controls.
    const workspaceSlots = Semaphore.makeUnsafe(8);
    const rpc = {
        ...rawRpc,
        InspectAnimations: (input, requestOptions) => workspaceSlots.withPermit(rawRpc.InspectAnimations(input, requestOptions)),
        ParseSubtitle: (input, requestOptions) => workspaceSlots.withPermit(rawRpc.ParseSubtitle(input, requestOptions)),
        LoadProject: (input, requestOptions) => workspaceSlots.withPermit(rawRpc.LoadProject(input, requestOptions)),
        SaveProject: (input, requestOptions) => workspaceSlots.withPermit(rawRpc.SaveProject(input, requestOptions)),
        ImportAsset: (input, requestOptions) => workspaceSlots.withPermit(rawRpc.ImportAsset(input, requestOptions)),
        ReadAsset: (input, requestOptions) => workspaceSlots.withPermit(rawRpc.ReadAsset(input, requestOptions)),
        ReadArtifact: (input, requestOptions) => workspaceSlots.withPermit(rawRpc.ReadArtifact(input, requestOptions)),
        RemoveArtifact: (input, requestOptions) => workspaceSlots.withPermit(rawRpc.RemoveArtifact(input, requestOptions)),
    };
    const run = (effect) => Effect.raceFirst(effect.pipe(Effect.catchCause((cause) => Effect.fail(asRenderError(Cause.squash(cause))))), Deferred.await(failure));
    const ping = run(rpc.Ping({ protocolVersion: PROTOCOL_VERSION })).pipe(Effect.timeoutOrElse({ duration: options.responseTimeoutMs, orElse: () => Effect.fail(workerError("timeout", "Worker heartbeat did not respond")) }));
    yield* run(rpc.Ping({ protocolVersion: PROTOCOL_VERSION })).pipe(Effect.timeoutOrElse({ duration: options.startupTimeoutMs, orElse: () => Effect.fail(workerError("timeout", "Worker startup handshake timed out")) }));
    yield* Effect.forever(Effect.sleep(options.heartbeatIntervalMs).pipe(Effect.andThen(ping))).pipe(Effect.catch((error) => Effect.sync(() => terminate(error))), Effect.forkIn(scope));
    return {
        rpc, run, terminate,
        receiveFile: (kind, transferId, request) => run(files.receive(kind, transferId, request, options.fileTimeoutMs)),
        sendAsset: (file, transferId) => Effect.try({ try: () => {
                if (closed)
                    throw workerError("unavailable", "Worker is closed");
                if (!(file instanceof File) || file.size <= 0 || file.size > MAX_ASSET_INPUT_BYTES)
                    throw workerError("protocol", "图片文件需大于 0 且不超过 64 MiB");
                const descriptor = Schema.decodeUnknownSync(NativeFileDescriptor)({ protocolVersion: PROTOCOL_VERSION, kind: "asset-input", transferId, byteLength: file.size });
                frameChannel.port1.postMessage({ descriptor, file });
            }, catch: asRenderError }),
    };
});
